// ============================================
// Light GPU Layouts — defineStruct-based GPU structs for lighting
// ============================================

import { defineStruct, GPUType } from '../core/GPUTypes';
import { graphicsConfig } from '../GraphicsConfig';

// ============================================================================
// GPU Light Struct  (storage buffer element)
// ============================================================================

/**
 * Per-light data written to the light storage buffer.
 *
 * WGSL equivalent:
 * ```wgsl
 * struct GPULight {
 *   positionAndRadius: vec4f,  // xy = position, z = radius, w = falloff
 *   colorAndIntensity: vec4f,  // rgb = color, a = intensity
 *   directionAndType:  vec4f,  // xy = direction, z = innerAngle, w = type (0=point,1=spot,2=directional)
 *   spotParams:        vec4f,  // x = outerAngle, y = shadowIndex (-1=none), z = shadowSoftness, w = shadowBias
 *   iesParams:         vec4f,  // x = iesIntensity (0-1), y = iesRotation (radians), z = iesProfileIndex (-1=none), w = reserved
 *   volumetricParams:  vec4f,  // x = volumetricIntensity, y = volumetricScattering, z = reserved, w = reserved
 * }
 * ```
 *
 * Layout: 96 bytes per light, 16-byte aligned (vec4 × 6).
 */
export const GPU_LIGHT_STRUCT = defineStruct('GPULight', {
  positionAndRadius: GPUType.f32().vec4(),   // xy=pos, z=radius, w=falloff
  colorAndIntensity: GPUType.f32().vec4(),   // rgb=color, a=intensity
  directionAndType: GPUType.f32().vec4(),    // xy=direction, z=innerAngle, w=type
  spotParams: GPUType.f32().vec4(),          // x=outerAngle, y=shadowIndex, z=shadowSoftness, w=shadowBias
  iesParams: GPUType.f32().vec4(),           // x=iesIntensity, y=iesRotation (rad), z=iesProfileIndex, w=reserved
  volumetricParams: GPUType.f32().vec4(),    // x=volumetricIntensity, y=volumetricScattering, z=reserved, w=reserved
});

export type GPULightFields = typeof GPU_LIGHT_STRUCT.types;

// ============================================================================
// Light Uniforms  (uniform buffer)
// ============================================================================

/**
 * Global lighting uniform data.
 *
 * WGSL equivalent:
 * ```wgsl
 * struct LightUniforms {
 *   ambientColor: vec4f,        // rgb = color, a = intensity
 *   lightCountAndFlags: vec4u,  // x = pointLightCount, y = spotLightCount, z = directionalLightCount, w = flags
 *   screenSize: vec2f,          // viewport width, height
 *   invScreenSize: vec2f,       // 1/width, 1/height
 *   invViewProjection: mat4x4f, // inverse view-projection (NDC → world)
 * }
 * ```
 */
export const LIGHT_UNIFORMS = defineStruct('LightUniforms', {
  ambientColor: GPUType.f32().vec4(),        // rgb = ambient color, a = ambient intensity
  lightCountAndFlags: GPUType.u32().vec4(),  // x=point, y=spot, z=directional, w=flags
  screenSize: GPUType.f32().vec2(),          // viewport dimensions
  invScreenSize: GPUType.f32().vec2(),       // 1 / viewport dimensions
  invViewProjection: GPUType.f32().mat4x4(),   // inverse view-projection (UV → world)
  // Forward+ config — APPENDED. Was GraphicsConfig pipeline `override` constants; now live uniforms because
  // Slang folds `f32(override)` into illegal WGSL module `const` (see sprite-lit.slang). Append-only: the
  // remaining WGSL lighting shaders read only the prefix above, so they're unaffected.
  fpConfig: GPUType.u32().vec4(),            // x=tileSize, y=maxLightsPerTile, z=shadowMapRes, w=iesProfileResolution
  pcfSamples: GPUType.i32().build(),         // shadow PCF tap count
});

export type LightUniformFields = typeof LIGHT_UNIFORMS.types;

// ============================================================================
// GPU Light Type Constants
// ============================================================================

/** Light type IDs matching the GPU encoding in `directionAndType.w`. */
export const GPULightType = {
  POINT: 0,
  SPOT: 1,
  DIRECTIONAL: 2,
} as const;

// ============================================================================
// Limits — read from GraphicsConfig (configurable at project level)
// ============================================================================

/** Maximum number of lights in the storage buffer. */
export function getMaxLights(): number { return graphicsConfig.maxLights; }

/** Maximum number of shadow-casting lights (rows in shadow atlas). */
export function getMaxShadowLights(): number { return graphicsConfig.maxShadowLights; }

/** Angular resolution of each 1D shadow map (samples per light). */
export function getShadowMapResolution(): number { return graphicsConfig.shadowMapResolution; }

/** Maximum number of shadow caster edges in the edge buffer. */
export function getMaxShadowEdges(): number { return graphicsConfig.maxShadowEdges; }

// Legacy named constants (deprecated — prefer graphicsConfig or getter functions)
// Kept for backward compat; these capture the initial default values.
export const MAX_LIGHTS = 1024;
export const MAX_SHADOW_LIGHTS = 128;
export const SHADOW_MAP_RESOLUTION = 512;
export const MAX_SHADOW_EDGES = 4096;

/** Size in bytes of a single GPULight entry. */
export const GPU_LIGHT_SIZE = GPU_LIGHT_STRUCT.size;

/** Total size of the light storage buffer in bytes. */
export const LIGHT_BUFFER_SIZE = GPU_LIGHT_SIZE * MAX_LIGHTS;

// Re-export graphicsConfig for convenient access
export { graphicsConfig } from '../GraphicsConfig';
